// src/features/merchantConfig/api/merchantConfigClient.js
//
// Thin wrapper over the portal's existing axios client (`src/services/api.js`)
// for the Merchant Config endpoints. Centralising it here means every consumer
// goes through one place — easier to swap auth / correlation IDs / telemetry
// later and easier to mock in tests.

import API from "../../../services/api";
import { API as ApiUrls } from "../../../utils/apiUrls";

/**
 * @typedef {Object} MerchantConfigEnvelope
 * @property {string} merchant_id
 * @property {string} merchant_configuration_key
 * @property {Record<string, unknown>} [merchant_general_configurations_value]
 * @property {Record<string, unknown>} [merchant_shipment_label_configurations_value]
 * @property {Record<string, unknown>} [merchant_shipment_invoice_configurations_value]
 * @property {Record<string, unknown>} [merchant_order_summary_report_configuration_value]
 * @property {Record<string, unknown>} [merchant_scheduled_email_report_configuration_value]
 * @property {Record<string, unknown>} [merchant_cod_remittance_configurations_value]
 * @property {Record<string, unknown>} [merchant_external_shipment_notes_configurations_value]
 * @property {Record<string, unknown>} [merchant_sms_notification_configurations_value]
 * @property {Record<string, unknown>} [merchant_whats_app_notification_configurations_value]
 * @property {Record<string, unknown>} [merchant_create_order_confirmation_configurations_value]
 * @property {Record<string, unknown>} [merchant_ndr_customer_interaction_configurations_value]
 * @property {Record<string, unknown>} [merchant_webhook_configurations_value]
 * @property {string} [created_date_time]
 * @property {string} [created_by]
 * @property {string} [updated_date_time]
 * @property {string} [updated_by]
 */

/**
 * @typedef {Object} MerchantConfigErrorContext
 * @property {string} [field_name]
 * @property {string} [actual_value]
 * @property {string} [reason]
 * @property {string} [merchant_id]
 * @property {string} [merchant_configuration_key]
 */

/**
 * @typedef {Object} MerchantConfigError
 * @property {number | undefined} status   - HTTP status (or 0 for network)
 * @property {string} error_code           - Canonical discriminator per API doc §6
 * @property {string} error_message
 * @property {MerchantConfigErrorContext} [error_context]
 * @property {boolean} [isNotFound]        - convenience flag (status 404 OR error_code RECORD_NOT_FOUND)
 */

/**
 * Normalise an axios / fetch error into the canonical MerchantConfigError
 * shape. The caller (TanStack Query hooks) drives the UI off `error_code`,
 * never the HTTP status, per the API doc.
 *
 * @param {unknown} rawError
 * @returns {MerchantConfigError}
 */
function normaliseError(rawError) {
  // Our portal axios interceptor in src/services/api.js wraps errors into
  // `{ message, code, status, details }`. `details` is the original response
  // body — which is where the OMS error envelope lives.
  const status = rawError && typeof rawError === "object" ? rawError.status : undefined;
  const body =
    rawError && typeof rawError === "object" && rawError.details
      ? rawError.details
      : null;

  const errorCode =
    (body && (body.error_code || body.errorCode)) ||
    (rawError && rawError.code) ||
    "UNKNOWN_ERROR";

  const errorMessage =
    (body && (body.error_message || body.errorMessage || body.message)) ||
    (rawError && rawError.message) ||
    "An unexpected error occurred";

  const errorContext = body ? body.error_context || body.errorContext : undefined;

  const isNotFound = status === 404 || errorCode === "RECORD_NOT_FOUND";

  return {
    status,
    error_code: errorCode,
    error_message: errorMessage,
    error_context: errorContext,
    isNotFound,
  };
}

/**
 * Fetch a single merchant configuration document.
 *
 * @param {{ merchantId: string, configKey: string, signal?: AbortSignal }} params
 * @returns {Promise<MerchantConfigEnvelope>}
 * @throws {MerchantConfigError}
 */
export async function fetchMerchantConfig({ merchantId, configKey, signal }) {
  if (!merchantId) {
    throw normaliseError({
      status: 400,
      details: {
        error_code: "PARAMETER_VALUE_CANNOT_BE_EMPTY",
        error_message: "merchantId is required",
        error_context: { field_name: "merchantId" },
      },
    });
  }
  if (!configKey) {
    throw normaliseError({
      status: 400,
      details: {
        error_code: "PARAMETER_VALUE_CANNOT_BE_EMPTY",
        error_message: "merchantConfigurationKey is required",
        error_context: { field_name: "merchantConfigurationKey" },
      },
    });
  }

  try {
    const url = ApiUrls.merchantConfigByKey(merchantId, configKey);
    const { data } = await API.get(url, { signal });
    return data;
  } catch (err) {
    throw normaliseError(err);
  }
}

/**
 * Partial-update (PATCH) a single merchant configuration. The body MUST be
 * the inner value block (see MERCHANT_CONFIG_API_DOCS §5.4), not the outer
 * envelope. Arrays in the body are wholesale-replaced server-side; objects
 * are recursively merged. Fields omitted are preserved.
 *
 * @param {{ merchantId: string, configKey: string, body: Record<string, unknown>, signal?: AbortSignal }} params
 * @returns {Promise<MerchantConfigEnvelope>} the full updated envelope
 * @throws {MerchantConfigError}
 */
export async function patchMerchantConfig({
  merchantId,
  configKey,
  body,
  signal,
}) {
  if (!merchantId) {
    throw normaliseError({
      status: 400,
      details: {
        error_code: "PARAMETER_VALUE_CANNOT_BE_EMPTY",
        error_message: "merchantId is required",
        error_context: { field_name: "merchantId" },
      },
    });
  }
  if (!configKey) {
    throw normaliseError({
      status: 400,
      details: {
        error_code: "PARAMETER_VALUE_CANNOT_BE_EMPTY",
        error_message: "merchantConfigurationKey is required",
        error_context: { field_name: "merchantConfigurationKey" },
      },
    });
  }
  if (!body || typeof body !== "object") {
    throw normaliseError({
      status: 400,
      details: {
        error_code: "PARAMETER_VALUE_CANNOT_BE_NULL",
        error_message: "PATCH body is required",
      },
    });
  }
  if (Object.keys(body).length === 0) {
    // Server returns PARAMETER_VALUE_CANNOT_BE_EMPTY for {} bodies. Fail
    // fast client-side with the same code so UI mapping is consistent.
    throw normaliseError({
      status: 400,
      details: {
        error_code: "PARAMETER_VALUE_CANNOT_BE_EMPTY",
        error_message: "Nothing to save — no fields were changed.",
      },
    });
  }

  try {
    const url = ApiUrls.merchantConfigByKey(merchantId, configKey);
    const { data } = await API.patch(url, body, {
      signal,
      headers: { "Content-Type": "application/json" },
    });
    return data;
  } catch (err) {
    throw normaliseError(err);
  }
}

// Re-exported for tests / consumers that need their own normalisation.
export const __testing = { normaliseError };
